import React, { useEffect, useMemo, useState, useRef } from "react";
import './horizonTable.css';
import { toast } from "react-toastify";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import {
  adminEditModalOverlayStyle,
  adminEditModalContentStyle,
  adminEditModalHeaderStyle,
  adminEditModalTitleStyle,
  adminEditModalBodyStyle,
  adminEditModalFooterStyle,
  adminEditCancelBtnStyle,
  adminEditSaveBtnStyle,
  adminEditLabelStyle,
  adminEditInputStyle,
  adminEditSigBoxStyle,
} from "./adminEditModalTheme";
import DatePicker from "react-datepicker";
import SignatureCanvas from "react-signature-canvas";
import jsPDF from 'jspdf';
import onsiteproLogo from '../../assets/logo2 2.png';
import { buildSharePointFileName, toDdMmYyyy } from '../../utils/formPdfHelpers';

import { canEditDeleteRecords } from "../../utils/rolePermissions";
function parseSiteRegisterDate(value) {
  if (!value) return null;
  const s = String(value).trim();
  const slash = s.split("/");
  if (slash.length === 3) {
    const [dd, mm, yyyy] = slash;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const dash = s.split("-");
  if (dash.length === 3) {
    if (dash[0].length === 4) {
      const d = new Date(Number(dash[0]), Number(dash[1]) - 1, Number(dash[2]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const [dd, mm, yyyy] = dash;
    const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatSiteRegisterDate(date) {
  if (!date) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

const siteRegisterDatePickerProps = {
  dateFormat: "dd-MM-yyyy",
  className: "form-control",
  placeholderText: "dd-MM-yyyy",
  todayButton: "Today",
  calendarClassName: "site-register-theme-calendar",
  popperClassName: "site-register-datepicker-popper",
  popperPlacement: "bottom-start",
  popperProps: { strategy: "fixed" },
};

const siteRegisterEditFieldStyle = {
  ...adminEditInputStyle,
  backgroundColor: "#fafafa",
  color: "#1f2937",
  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
};

const siteRegisterEditSigStyle = {
  ...adminEditSigBoxStyle(),
  minHeight: 120,
  background: "linear-gradient(180deg, #fdf8f9 0%, #f5eaed 100%)",
  border: "1.5px solid #e2c4cc",
  boxShadow: "inset 0 1px 4px rgba(139,22,52,0.05)",
};

function SiteRegisterPage() {
  const canEditDelete = canEditDeleteRecords();
  const [appUsers, setAppUsers] = useState([]);

  const [registers, setRegisters] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 4;

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editRegister, setEditRegister] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const isAdminUser = adminIsPrimaryAdmin();

  const [newRegister, setNewRegister] = useState({
    formUuid: "",
    surname: '',
    first_name: '',
    job_title_department: '',
    site_location: '',
    date: '',
    time_in: '',
    time_out: '',
    signature: ''
  });
  const [editSignature, setEditSignature] = useState("");
  const sigCanvasRef = useRef(null);
  const editSigCanvasRef = useRef(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const generatingPdfRef = useRef(false);
  
  // SharePoint upload modal state
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState('phase');
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState('15');
  const [sharePointNetwork, setSharePointNetwork] = useState('');
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);
  // Signature pad handlers for Add Modal
  const clearSignature = () => {
    sigCanvasRef.current.clear();
    setNewRegister(prev => ({ ...prev, signature: "" }));
  };
  const saveSignature = () => {
    if (!sigCanvasRef.current.isEmpty()) {
      setNewRegister(prev => ({
        ...prev,
        signature: sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png")
      }));
    }
  };

  // Signature pad handlers for Edit Modal
  const clearEditSignature = () => {
    if (editSigCanvasRef.current) {
    editSigCanvasRef.current.clear();
    }
    setEditSignature("");
    setEditRegister(prev => ({ ...prev, signature: "" }));
  };
  const saveEditSignature = () => {
    if (editSigCanvasRef.current && !editSigCanvasRef.current.isEmpty()) {
      // Get the full canvas instead of trimmed to maintain proper size
      const canvas = editSigCanvasRef.current.getCanvas();
      const sig = canvas.toDataURL("image/png");
      setEditSignature(sig);
      setEditRegister(prev => ({ ...prev, signature: sig }));
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    async function fetchRegisters() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/site-register", {
          headers: { "Authorization": `Bearer ${token}` },
          signal: ac.signal,
        });
        const data = await res.json();
        if (ac.signal.aborted) return;
        if (res.ok && data.success && Array.isArray(data.data)) {
          setRegisters(data.data);
        } else {
          setRegisters([]);
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        setRegisters([]);
      }
    }
    async function fetchAppUsers() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
        const token = localStorage.getItem("token");
        const res = await fetch(baseUrl + "/api/app-users", {
          headers: { "Authorization": `Bearer ${token}` },
          signal: ac.signal,
        });
        const data = await res.json();
        if (ac.signal.aborted) return;
        if (res.ok && data.success && Array.isArray(data.data)) {
          setAppUsers(data.data);
        } else {
          setAppUsers([]);
        }
      } catch (err) {
        if (err?.name === "AbortError") return;
        setAppUsers([]);
      }
    }
    fetchRegisters();
    fetchAppUsers();
    return () => ac.abort();
  }, []);

  const formatDate = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? d : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Initialize signature canvas when edit modal opens
  useEffect(() => {
    if (showEditModal && editSigCanvasRef.current) {
      setTimeout(() => {
        if (editSigCanvasRef.current) {
          editSigCanvasRef.current.clear();
        }
      }, 100);
    }
  }, [showEditModal]);

  const filtered = useMemo(() => {
    const searchWords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return registers.filter((r) => {
      const fields = [r.surname, r.first_name, r.site_location].map((val) => (val || "").toLowerCase());
      if (searchWords.length === 0) return true;
      return searchWords.every((word) => fields.some((field) => field.includes(word)));
    });
  }, [registers, search]);

  useEffect(() => {
    if (filtered.length === 0 && page > 1) {
      setPage(1);
    }
  }, [filtered.length, page]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page-1)*pageSize, page*pageSize);

  // Add Register
  const handleAdd = async () => {
    // Basic validation
    if (!newRegister.surname || !newRegister.first_name || !newRegister.site_location || !newRegister.date || !newRegister.time_in || !newRegister.time_out || !newRegister.job_title_department) {
      toast.error("All fields are required");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Get userId from localStorage - backend will automatically find user and save firstName/lastName
      const userId = localStorage.getItem("userId");

      const ensureUuid =
        newRegister.formUuid ||
        (window.crypto && typeof window.crypto.randomUUID === "function"
          ? window.crypto.randomUUID()
          : String(Date.now()));
      
      const registerToSave = {
        ...newRegister,
        formUuid: ensureUuid,
        userId: userId || ''
      };
      
      const res = await fetch(baseUrl + "/api/site-register", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(registerToSave)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Refresh the registers list to get the updated data with addedByFirstName and addedByLastName
        const refreshRes = await fetch(baseUrl + "/api/site-register", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const refreshData = await refreshRes.json();
        if (refreshRes.ok && refreshData.success && Array.isArray(refreshData.data)) {
          setRegisters(refreshData.data);
        }
        setShowAddModal(false);
        setNewRegister({ formUuid: "", surname: '', first_name: '', job_title_department: '', site_location: '', date: '', time_in: '', time_out: '', signature: '' });
        toast.success("Site register added!");
      } else {
        toast.error(data.message || "Failed to add");
      }
    } catch {
      toast.error("Failed to add");
    }
  };

  // Edit Register
  const handleEdit = (reg) => {
    const ensureUuid =
      reg?.formUuid ||
      (window.crypto && typeof window.crypto.randomUUID === "function"
        ? window.crypto.randomUUID()
        : String(Date.now()));
    setEditRegister({ ...reg, formUuid: ensureUuid });
    setEditSignature(reg.signature || "");
    setShowEditModal(true);
    setTimeout(() => {
      if (editSigCanvasRef.current) {
        editSigCanvasRef.current.clear();
        if (reg.signature) {
        let sigSrc = reg.signature;
        // If it's just base64, prepend the header
        if (!sigSrc.startsWith('data:image')) {
          sigSrc = 'data:image/png;base64,' + sigSrc;
        }
         const img = new window.Image();
         img.onload = () => {
            if (editSigCanvasRef.current) {
              const canvas = editSigCanvasRef.current.getCanvas();
              const ctx = canvas.getContext('2d');
              ctx.clearRect(0, 0, canvas.width, canvas.height);
              // Draw the image with proper scaling to maintain aspect ratio
              const canvasAspect = canvas.width / canvas.height;
              const imgAspect = img.width / img.height;
              
              let drawWidth, drawHeight, drawX, drawY;
              
              if (imgAspect > canvasAspect) {
                // Image is wider than canvas
                drawWidth = canvas.width;
                drawHeight = canvas.width / imgAspect;
                drawX = 0;
                drawY = (canvas.height - drawHeight) / 2;
              } else {
                // Image is taller than canvas
                drawHeight = canvas.height;
                drawWidth = canvas.height * imgAspect;
                drawX = (canvas.width - drawWidth) / 2;
                drawY = 0;
              }
              
              ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
            }
          };
          img.onerror = () => {
            console.error('Failed to load signature image');
          };
          img.src = sigSrc;
        }
      }
    }, 300);
  };
  const handleEditSave = async () => {
    if (!editRegister.site_location || !editRegister.date) {
      toast.error("Date and Site Location are required");
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      
      // Backend will automatically find user by userId and update firstName/lastName
      const registerToSave = {
        ...editRegister,
        formUuid:
          editRegister?.formUuid ||
          (window.crypto && typeof window.crypto.randomUUID === "function"
            ? window.crypto.randomUUID()
            : String(Date.now())),
      };
      
      const res = await fetch(baseUrl + `/api/site-register/${editRegister._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(registerToSave)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        // Refresh the registers list to get the updated data with addedByFirstName and addedByLastName
        const refreshRes = await fetch(baseUrl + "/api/site-register", {
          headers: { "Authorization": `Bearer ${token}` }
        });
        const refreshData = await refreshRes.json();
        if (refreshRes.ok && refreshData.success && Array.isArray(refreshData.data)) {
          setRegisters(refreshData.data);
        }
        setShowEditModal(false);
        toast.success("Site register updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  // Delete Register
  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
      const token = localStorage.getItem("token");
      const res = await fetch(baseUrl + `/api/site-register/${deleteId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setRegisters(registers.filter(r => r._id !== deleteId));
        toast.success("Deleted successfully!");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // SharePoint Upload Handler
  const handleSharePointUpload = async () => {
    const locationForUpload = (sharePointUploadRecord?.site_location || '').trim();

    if (!locationForUpload) {
      toast.error('Site location is missing for this record.');
      return;
    }

    if (sharePointPhaseType === 'phase' && (!sharePointNetwork || sharePointNetwork.trim() === '')) {
      toast.error('Please select an area (EPN, LPN, or SPN)');
      return;
    }

    const payloadPhaseType = sharePointPhaseType === 'phase' ? 'phase' : 'non-phase';
    const payloadNetwork = sharePointPhaseType === 'phase' ? sharePointNetwork : '';
    const payloadPhaseNumber = sharePointPhaseType === 'phase' ? sharePointPhaseNumber : '';

    setShowSharePointUploadModal(false);

    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType('phase');
        setSharePointPhaseNumber('15');
        setSharePointNetwork('');
      },
      onError: () => {}
    });
  };

  // Generate PDF for site register entry
  const generatePDF = async (register, options = {}) => {
      const shouldUploadToSharePoint = options.uploadOnly === true;
      const uploadConfig = shouldUploadToSharePoint ? {
        phaseType: options.phaseType || 'non-phase',
        phaseNumber: options.phaseNumber || '',
        network: options.network || '',
        siteName: options.siteName || '',
        onSuccess: options.onSuccess,
        onError: options.onError
      } : null;
      
      const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Color scheme
    const primaryColor = [139, 22, 52]; // #8b1634
    const secondaryColor = [233, 207, 214]; // #e9cfd6
    const darkGray = [64, 64, 64];
    const lightGray = [128, 128, 128];
    
    // Function to load logo and add to PDF
    const addLogoToPDF = () => {
      return new Promise((resolve) => {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        
        logoImg.onload = function() {
          try {
            // Add logo directly to PDF with increased width and decreased height
            doc.addImage(logoImg, 'PNG', 5, 5, 60, 15);
            resolve();
          } catch (error) {
            // Fallback to simple logo if image processing fails
            doc.setFillColor(255, 255, 255);
            doc.rect(5, 5, 60, 15, 'F');
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
            doc.text("LOGO", 35, 12);
            resolve();
          }
        };
        
        logoImg.onerror = function() {
          // Fallback to simple logo if image loading fails
          doc.setFillColor(255, 255, 255);
          doc.rect(5, 5, 60, 15, 'F');
          doc.setFont("helvetica", "bold");
          doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
          doc.text("LOGO", 35, 12);
          resolve();
        };
        
        logoImg.src = onsiteproLogo;
      });
    };
    
    // Helper function to draw rounded rectangle
    const drawRoundedRect = (x, y, width, height, radius, fillColor, strokeColor) => {
      doc.setFillColor(...fillColor);
      doc.setDrawColor(...strokeColor);
      doc.setLineWidth(0.5);
      doc.roundedRect(x, y, width, height, radius, radius, 'FD');
    };
    
    // Helper function to add section header
    const addSectionHeader = (text, y) => {
      // Background for section header
      drawRoundedRect(15, y - 5, pageWidth - 30, 12, 2, secondaryColor, primaryColor);
      
      // Section title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.setTextColor(...primaryColor);
      doc.text(text, 20, y + 2);
      
      return y + 15;
    };
    
    // Helper function to capitalize first letter
    const capitalizeFirstLetter = (str) => {
      if (!str || typeof str !== 'string') return str;
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    // Helper function to add data row
    const addDataRow = (label, value, y, isHighlight = false) => {
      const labelWidth = 60;
      const valueWidth = pageWidth - 100;

      // Label
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...darkGray);
      doc.text(label + ":", 20, y);
      
      // Value
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(...darkGray);
      
      // Capitalize first letter of string values
      const formattedValue = typeof value === 'string' ? capitalizeFirstLetter(value) : (value || 'N/A');
      
      // Handle long text wrapping
      const lines = doc.splitTextToSize(formattedValue, valueWidth);
      doc.text(lines, 25 + labelWidth, y);

      return y + (lines.length * 4) + 3;
    };
    
    // Header Section with Company Branding (reduced height)
    drawRoundedRect(0, 0, pageWidth, 25, 0, primaryColor, primaryColor);
    
    // Add OnSitePro logo - wait for it to load
    await addLogoToPDF();
    
    // Main title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text("SITE REGISTER ENTRY", 75, 12);
    
    // Subtitle
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(255, 255, 255);
    doc.text("Official Site Visit Documentation", 75, 18);
    
    // Document ID and Date
    const today = new Date();
    const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;
    
    let yPosition = 35;
    
    // Site Register Information Section (top-level: Date + Site Location only)
    yPosition = addSectionHeader("SITE REGISTER INFORMATION", yPosition);
    yPosition = addDataRow("Site Location", register.site_location, yPosition);
    yPosition = addDataRow("Date", register.date, yPosition);
    yPosition += 5;

    // Operatives — new structure. Fall back to the old single-person fields for
    // older records that don't have an operatives array.
    const operativesList =
      Array.isArray(register.operatives) && register.operatives.length > 0
        ? register.operatives
        : (register.first_name || register.surname || register.signature
            ? [{
                first_name: register.first_name,
                surname: register.surname,
                job_title_department: register.job_title_department,
                time_in: register.time_in,
                time_out: register.time_out,
                signature: register.signature,
              }]
            : []);

    yPosition = addSectionHeader("OPERATIVES", yPosition);

    if (operativesList.length === 0) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...lightGray);
      doc.text("No operatives recorded.", 25, yPosition);
      yPosition += 12;
    } else {
      for (let i = 0; i < operativesList.length; i++) {
        const op = operativesList[i] || {};

        // Page break if not enough space for this operative block.
        if (yPosition > pageHeight - 70) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(...primaryColor);
        doc.text(`Operative ${i + 1}`, 20, yPosition);
        yPosition += 6;

        yPosition = addDataRow("First Name", op.first_name, yPosition);
        yPosition = addDataRow("Surname", op.surname, yPosition);
        yPosition = addDataRow("Job Title/Department", op.job_title_department, yPosition);
        yPosition = addDataRow("Time In", op.time_in, yPosition);
        yPosition = addDataRow("Time Out", op.time_out, yPosition);

        // Signature (image)
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        doc.setTextColor(...darkGray);
        doc.text("Signature:", 20, yPosition);
        if (op.signature) {
          try {
            const sigData = op.signature.startsWith('data:image')
              ? op.signature
              : `data:image/png;base64,${op.signature}`;
            // Preserve the signature's real aspect ratio so it doesn't stretch.
            const maxW = 50, maxH = 16;
            let drawW = maxW, drawH = maxH;
            try {
              const props = doc.getImageProperties(sigData);
              if (props && props.width && props.height) {
                const ratio = props.width / props.height;
                drawW = maxW;
                drawH = drawW / ratio;
                if (drawH > maxH) { drawH = maxH; drawW = drawH * ratio; }
              }
            } catch (e) { /* fall back to max box */ }
            doc.addImage(sigData, 'PNG', 85, yPosition - 6, drawW, drawH);
            yPosition += Math.max(16, drawH + 2);
          } catch (error) {
            doc.setFont("helvetica", "normal");
            doc.setTextColor(...lightGray);
            doc.text("Could not load", 85, yPosition);
            yPosition += 8;
          }
        } else {
          doc.setFont("helvetica", "normal");
          doc.setTextColor(...lightGray);
          doc.text("N/A", 85, yPosition);
          yPosition += 8;
        }

        // Divider + gap between operatives
        yPosition += 3;
        doc.setDrawColor(230, 230, 230);
        doc.setLineWidth(0.3);
        doc.line(20, yPosition, pageWidth - 20, yPosition);
        yPosition += 8;
      }
    }
    
    // Footer Section
    const footerY = pageHeight - 25;
    drawRoundedRect(0, footerY, pageWidth, 25, 0, [248, 249, 250], [200, 200, 200]);
    
    // Footer content
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...lightGray);
    doc.text(`Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 20, footerY + 8);
    
    // Page number
    doc.text("Page 1 of 1", pageWidth - 30, footerY + 15);
    
    // Add decorative border
    doc.setDrawColor(...primaryColor);
    doc.setLineWidth(1);
    doc.rect(5, 5, pageWidth - 10, pageHeight - 10);
    
    // Save the PDF with first name and SiteRegister
    const safeFirstName = String(register.first_name || 'SiteRegister').replace(/[\\/:"*?<>|]+/g, '-');
    
    let fileName;
    if (shouldUploadToSharePoint && uploadConfig) {
      fileName = buildSharePointFileName([
        register.first_name,
        register.site_location,
        toDdMmYyyy(register.date),
      ]);
    } else {
      fileName = `${safeFirstName}_SiteRegister.pdf`;
    }
    
    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${register._id || ''}|${register.site_location || ''}|${register.date || ''}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const dataUri = doc.output('datauristring');
        
        // Prepare SharePoint upload parameters
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          phaseType: uploadConfig.phaseType,
          phaseNumber: uploadConfig.phaseNumber,
          network: uploadConfig.network,
          siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "Site Register"
        };
        
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: 'POST',
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = 'SharePoint upload failed';
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch (_) {}
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) {
          uploadConfig.onSuccess();
        }
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) {
          uploadConfig.onError(err);
        }
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("Professional PDF generated successfully!");
    }
  };

  return (
    <>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
        padding: '24px',
        marginBottom: '24px'
      }}>
        {/* Header Section */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#1a202c',
            margin: 0
          }}>Site Register</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: '#8b1634',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 14px',
                  fontWeight: '600',
                  fontSize: '13px',
                  cursor: 'pointer'
                }}
              >
                Import File
              </button>
            )}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <i className="fa fa-search" style={{
                position: 'absolute',
                left: '12px',
                color: '#8b1634',
                fontSize: '14px'
              }}></i>
              <input 
                type="text" 
                placeholder="Type to Search" 
                value={search} 
                onChange={e=>{ setSearch(e.target.value); setPage(1); }}
                style={{
                  padding: '10px 12px 10px 36px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  width: '250px',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#8b1634'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div style={{
          overflowX: 'auto',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          {/* Table Header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1.2fr 1fr 0.8fr 0.8fr 1fr 1fr 1.2fr',
            background: '#f7fafc',
            padding: '12px 16px',
            borderBottom: '1px solid #e2e8f0',
            gap: '16px',
            alignItems: 'center'
          }}>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>First Name</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Surname</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Site Location</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Time In</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Time Out</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Added By</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Updated Date</div>
            <div style={{fontWeight: '600', color: '#8b1634', fontSize: '14px'}}>Actions</div>
          </div>

          {/* Table Body */}
          <div>
            {paged.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                color: '#718096',
                fontSize: '16px'
              }}>No records found.</div>
            ) : paged.map((r, idx) => {
                // First check if firstName and lastName are saved in the record
                // If not, try to find user from appUsers array
                let userName = '-';
                if (r.addedByFirstName && r.addedByLastName) {
                  userName = `${r.addedByFirstName} ${r.addedByLastName}`;
                } else if (r.userId) {
                  const user = appUsers.find(u => String(u._id) === String(r.userId));
                  userName = user ? `${user.firstName} ${user.lastName}` : (r.userId || '-');
                }
                return (
                  <div 
                    key={r._id || idx}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1.2fr 1fr 0.8fr 0.8fr 1fr 1fr 1.2fr',
                      padding: '16px',
                      borderBottom: '1px solid #f1f5f9',
                      gap: '16px',
                      alignItems: 'center',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.first_name || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.surname || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.site_location || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.date || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.time_in || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{r.time_out || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{userName || '—'}</div>
                    <div style={{color: '#2d3748', fontSize: '14px'}}>{formatDate(r.updatedAt || r.createdAt)}</div>
                    <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleEdit(r)}
                          title="Edit"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-edit" style={{color:'#22c55e', fontSize: '16px'}}></i>
                        </button>
                      )}
                      <button 
                        onClick={()=>generatePDF(r)}
                        title="Generate PDF"
                        style={{
                          background: 'transparent',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '6px',
                          borderRadius: '6px',
                          transition: 'background-color 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <i className="fa fa-file-pdf-o" style={{color:'#dc2626', fontSize: '16px'}}></i>
                      </button>
                      {(() => { 
                        const k = `${r._id || ''}|${r.site_location || ''}|${r.date || ''}`; 
                        const busy = uploadingKey === k; 
                        return (
                          <button 
                            onClick={()=>{
                              if (!busy) {
                                setSharePointUploadRecord(r);
                                setSharePointPhaseType('phase');
                                setSharePointPhaseNumber('15');
                                setSharePointNetwork('');
                                setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                              }
                            }}
                            disabled={busy}
                            title={busy ? "Uploading..." : "Upload to SharePoint"}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: busy ? 'not-allowed' : 'pointer',
                              padding: '6px',
                              borderRadius: '6px',
                              transition: 'background-color 0.2s',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: busy ? 0.6 : 1
                            }}
                            onMouseEnter={(e) => !busy && (e.currentTarget.style.backgroundColor = '#f0fdf4')}
                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{color:'#22c55e', fontSize: '16px'}}></i>
                          </button>
                        ); 
                      })()}
                      {canEditDelete && (
                        <button 
                          onClick={()=>handleDelete(r._id)}
                          title="Delete"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fef2f2'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <i className="fa fa-trash" style={{color:'#e53e3e', fontSize: '16px'}}></i>
                        </button>
                      )}
                      </div>
                  </div>
                );
            })}
        </div>
        </div>

        {/* Pagination */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          marginTop: '18px',
          gap: '12px'
        }}>
          <button 
            className="horizon-table-btn" 
            disabled={page===1 || filtered.length === 0} 
            onClick={()=>setPage(page-1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===1 || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===1 || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===1 || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Prev</button>
          <span style={{
            fontWeight: '700',
            color: '#8b1634',
            fontSize: '16px'
          }}>{page} / {totalPages}</span>
          <button 
            className="horizon-table-btn" 
            disabled={page===totalPages || filtered.length === 0} 
            onClick={()=>setPage(page+1)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: '1px solid #e2e8f0',
              background: (page===totalPages || filtered.length === 0) ? '#f7fafc' : '#ffffff',
              color: (page===totalPages || filtered.length === 0) ? '#a0aec0' : '#8b1634',
              cursor: (page===totalPages || filtered.length === 0) ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >Next</button>
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Add Site Register</h5>
                <button type="button" className="btn-close" onClick={()=>setShowAddModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row mb-2">
                  <div className="col-4">
                    <label>Surname</label>
                    <input type="text" className="form-control" value={newRegister.surname} onChange={e=>setNewRegister({...newRegister, surname: e.target.value})} />
                  </div>
                  <div className="col-4">
                    <label>First Name</label>
                    <input type="text" className="form-control" value={newRegister.first_name} onChange={e=>setNewRegister({...newRegister, first_name: e.target.value})} />
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-4">
                    <label>Job Title/Department</label>
                    <input type="text" className="form-control" value={newRegister.job_title_department} onChange={e=>setNewRegister({...newRegister, job_title_department: e.target.value})} />
                  </div>
                  <div className="col-4">
                    <label>Site Location</label>
                    <input type="text" className="form-control" value={newRegister.site_location} onChange={e=>setNewRegister({...newRegister, site_location: e.target.value})} />
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-4">
                    <label>Date</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(newRegister.date)}
                        onChange={(d) => setNewRegister({ ...newRegister, date: formatSiteRegisterDate(d) })}
                      />
                    </div>
                  </div>
                  <div className="col-4">
                    <label>Time In</label>
                    <input type="time" className="form-control" value={newRegister.time_in} onChange={e=>setNewRegister({...newRegister, time_in: e.target.value})} />
                  </div>
                  <div className="col-4">
                    <label>Time Out</label>
                    <input type="time" className="form-control" value={newRegister.time_out} onChange={e=>setNewRegister({...newRegister, time_out: e.target.value})} />
                  </div>
                </div>
                <div className="col-md-6 mb-2">
                  <label className="form-label fw-semibold text-secondary">Signature</label>
                  <div style={{
                    width: '100%',
                    border: '2px solid #8b1634',
                    borderRadius: '18px',
                    background: '#e9cfd6',
                    padding: '0.5rem',
                    textAlign: 'left',
                    position: 'relative',
                    minHeight: '120px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <SignatureCanvas
                      ref={sigCanvasRef}
                      penColor="#8b1634"
                      backgroundColor="#e9cfd6"
                      clearOnResize={false}
                      velocityFilterWeight={0.7}
                      minWidth={1}
                      maxWidth={2.5}
                      dotSize={1.5}
                      throttle={16}
                      canvasProps={{
                        width: 350,
                        height: 100,
                        style: {
                          width: '100%',
                          height: '100px',
                          border: 'none',
                          borderRadius: '18px',
                          background: '#e9cfd6',
                          display: 'block',
                          touchAction: 'none'
                        }
                      }}
                      onEnd={saveSignature}
                    />
                    <div style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginTop: '4px'
                    }}>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm rounded-pill px-3"
                        style={{
                          background: 'transparent',
                          color: '#8b1634',
                          borderColor: '#8b1634'
                        }}
                        onClick={clearSignature}>
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowAddModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{background:'#8b1634', color:'#fff', fontWeight:600}} onClick={handleAdd}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editRegister && (
        <div className="modal fade show" style={adminEditModalOverlayStyle} tabIndex="-1">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={adminEditModalContentStyle}>
              <div className="modal-header" style={adminEditModalHeaderStyle}>
                <h5 className="modal-title" style={adminEditModalTitleStyle}>
                  <i className="fa fa-edit" style={{ marginRight: 8, fontSize: 16, opacity: 0.9 }}></i>
                  Edit Site Register
                </h5>
                <button type="button" className="btn-close btn-close-white" onClick={()=>setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={adminEditModalBodyStyle}>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Date</label>
                    <div className="site-register-datepicker-wrap">
                      <DatePicker
                        {...siteRegisterDatePickerProps}
                        selected={parseSiteRegisterDate(editRegister.date)}
                        onChange={(d) => setEditRegister({ ...editRegister, date: formatSiteRegisterDate(d) })}
                      />
                    </div>
                  </div>
                  <div className="col-12 col-md-6">
                    <label style={adminEditLabelStyle}>Site Location</label>
                    <input type="text" className="form-control" style={siteRegisterEditFieldStyle} value={editRegister.site_location} onChange={e=>setEditRegister({...editRegister, site_location: e.target.value})} />
                  </div>
                </div>
                <div style={{ borderTop: "1px solid #f0f0f0", paddingTop: 16, marginTop: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <span style={{ fontWeight: 700, color: "#1a202c", fontSize: 16 }}>Operatives</span>
                    <span style={{ background: "#fbeef2", color: "#8b1634", borderRadius: 999, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>
                      {(editRegister.operatives || []).length}
                    </span>
                  </div>
                  {(editRegister.operatives || []).length === 0 ? (
                    <div style={{ color: "#9ca3af", fontSize: 13, padding: "8px 0" }}>No operatives added.</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                      {(editRegister.operatives || []).map((op, idx) => {
                        const updateOp = (field, val) => {
                          const list = [...(editRegister.operatives || [])];
                          list[idx] = { ...list[idx], [field]: val };
                          setEditRegister({ ...editRegister, operatives: list });
                        };
                        return (
                          <div key={idx} style={{ border: "1px solid #edd5df", borderRadius: 12, overflow: "hidden", background: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#faf3f6", padding: "9px 14px", borderBottom: "1px solid #f0e6ea" }}>
                              <span style={{ fontWeight: 700, color: "#8b1634", fontSize: 13 }}>
                                <i className="fa fa-user" style={{ marginRight: 8, opacity: 0.75 }} />
                                Operative {idx + 1}
                              </span>
                              <button
                                type="button"
                                title="Remove operative"
                                style={{ background: "transparent", border: "none", color: "#e53e3e", cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, padding: 0 }}
                                onClick={() => setEditRegister({ ...editRegister, operatives: (editRegister.operatives || []).filter((_, i) => i !== idx) })}
                              >
                                <i className="fa fa-trash" />Remove
                              </button>
                            </div>
                            <div style={{ padding: 16 }}>
                              <div className="row g-3">
                                <div className="col-12 col-md-4">
                                  <label style={adminEditLabelStyle}>First Name</label>
                                  <input type="text" className="form-control" style={siteRegisterEditFieldStyle} value={op.first_name || ""} onChange={e => updateOp("first_name", e.target.value)} />
                                </div>
                                <div className="col-12 col-md-4">
                                  <label style={adminEditLabelStyle}>Surname</label>
                                  <input type="text" className="form-control" style={siteRegisterEditFieldStyle} value={op.surname || ""} onChange={e => updateOp("surname", e.target.value)} />
                                </div>
                                <div className="col-12 col-md-4">
                                  <label style={adminEditLabelStyle}>Job Title/Department</label>
                                  <input type="text" className="form-control" style={siteRegisterEditFieldStyle} value={op.job_title_department || ""} onChange={e => updateOp("job_title_department", e.target.value)} />
                                </div>
                              </div>
                              <div className="row g-3 mt-1">
                                <div className="col-6 col-md-3">
                                  <label style={adminEditLabelStyle}>Time In</label>
                                  <input type="time" className="form-control" style={siteRegisterEditFieldStyle} value={op.time_in || ""} onChange={e => updateOp("time_in", e.target.value)} />
                                </div>
                                <div className="col-6 col-md-3">
                                  <label style={adminEditLabelStyle}>Time Out</label>
                                  <input type="time" className="form-control" style={siteRegisterEditFieldStyle} value={op.time_out || ""} onChange={e => updateOp("time_out", e.target.value)} />
                                </div>
                                <div className="col-12 col-md-6">
                                  <label style={adminEditLabelStyle}>Signature</label>
                                  <div style={siteRegisterEditSigStyle}>
                                    {op.signature ? (
                                      <img
                                        src={op.signature.startsWith('data:image') ? op.signature : `data:image/png;base64,${op.signature}`}
                                        alt="Signature"
                                        style={{ maxWidth: "100%", maxHeight: "80px", width: "auto", height: "auto", objectFit: "contain", display: "block" }}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                      />
                                    ) : (
                                      <span style={{ fontSize: 13, color: "#9ca3af" }}>No signature recorded</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer d-flex justify-content-between align-items-center" style={adminEditModalFooterStyle}>
                <button type="button" className="btn" style={adminEditCancelBtnStyle} onClick={()=>setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={adminEditSaveBtnStyle} onClick={handleEditSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SharePoint Upload Modal */}
      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'600px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={()=>{
                  setShowSharePointUploadModal(false);
                  setSharePointUploadRecord(null);
                  setSharePointPhaseType('phase');
                  setSharePointPhaseNumber('15');
                  setSharePointNetwork('');
                }}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{color:'red'}}>*</span></label>
                    <select 
                      className="form-select" 
                      value={sharePointPhaseType}
                      onChange={(e) => {
                        setSharePointPhaseType(e.target.value);
                        if (e.target.value === 'non-phase') {
                          setSharePointNetwork('');
                        }
                      }}
                    >
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  
                  {sharePointPhaseType === 'phase' && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointPhaseNumber}
                          onChange={(e) => {
                            setSharePointPhaseNumber(e.target.value);
                          }}
                        >
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{color:'red'}}>*</span></label>
                        <select 
                          className="form-select" 
                          value={sharePointNetwork}
                          onChange={(e) => {
                            setSharePointNetwork(e.target.value);
                          }}
                        >
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                  
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={()=>{
                    setShowSharePointUploadModal(false);
                    setSharePointUploadRecord(null);
                    setSharePointPhaseType('phase');
                    setSharePointPhaseNumber('15');
                    setSharePointNetwork('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn" 
                  style={{background:'#8b1634', color:'#fff', fontWeight:600}}
                  onClick={handleSharePointUpload}
                >
                  Upload to SharePoint
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload Site Register File"
        importPath="site-register/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setRegisters((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="modal fade show" style={{display:'block', background:'rgba(0,0,0,0.3)'}} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{maxWidth:'1200px', margin:'0 auto'}}>
              <div className="modal-header">
                <h5 className="modal-title">Delete Site Register</h5>
                <button type="button" className="btn-close" onClick={()=>setShowDeleteModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this record?</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SiteRegisterPage;
